import * as React from 'react';
import { cn } from '../cn';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  width?: string | number;
  height?: string | number;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  /** Întârziere ms — pentru efect „valură" diagonal pe liste */
  delay?: number;
}

const ROUNDED: Record<NonNullable<SkeletonProps['rounded']>, string> = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  full: 'rounded-full',
};

/**
 * Skeleton placeholder cu shimmer subtil — atom de bază pentru loading states.
 * Folosit fie direct (`<Skeleton width="80%" height={16} />`), fie compus în
 * `TableSkeleton` / `FormSkeleton` / `CardSkeleton`.
 *
 * Aestetic intent: refined, mimetic — formele coincid cu conținutul real
 * pentru tranziție smooth după hidratare. Animația respectă reduce-motion.
 */
export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  function Skeleton({ className, width, height, rounded = 'md', delay = 0, style, ...rest }, ref) {
    return (
      <div
        ref={ref}
        role="presentation"
        aria-hidden="true"
        className={cn(
          // Base block — neutral gri, contrast moderat
          'relative overflow-hidden bg-neutral-200/80 dark:bg-neutral-800/70',
          ROUNDED[rounded],
          // Shimmer overlay (after) — gradient sweep stâng→drept
          'after:content-[""] after:absolute after:inset-0',
          'after:bg-gradient-to-r after:from-transparent after:via-white/70 after:to-transparent',
          'dark:after:via-white/[0.06]',
          'after:animate-shimmer',
          'motion-reduce:after:animate-none',
          className,
        )}
        style={{
          width,
          height: height ?? (width ? undefined : '0.875rem'),
          animationDelay: delay ? `${delay}ms` : undefined,
          ...(delay && { '--shimmer-delay': `${delay}ms` } as React.CSSProperties),
          ...style,
        }}
        {...rest}
      />
    );
  },
);
